import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Input,
  DropdownShell,
  Separator,
  Spinner,
} from '@design-systems/canvas-ui'
import { DomainGroup } from './DomainGroup'
import { ViewAllResults } from './ViewAllResults'
import { RecentSearches } from './RecentSearches'
import { Grid } from '@design-systems/canvas-ui'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react'
import {
  FEDERATED_DELETE_RECENTLY_SEARCHED,
  FEDERATED_GET_RECENT_SEARCHES,
  FEDERATED_ADD_RECENTLY_SEARCHED,
  RECENT_SEARCH_REFETCH_QUERIES,
} from '../../../../graphql/queries/globalSearch/recentSearchesQueries'
import styles from './GlobalSearchSlot.module.css'
import { FEDERATED_GET_GLOBAL_TOP_LEVEL_SEARCHES } from '../../../../graphql/queries/globalSearch/globalSearchQueries'
import {
  getBoldText,
  handleKeyDown,
  preventToggleWhenOpen,
  setInputFocus,
  getResourceLink,
  getDomainIcon,
  getDomainIconColor,
} from '../../utils/globalSearchHelpers'
import {
  DEVELOPMENT_ACCUMULATOR_DOMAINS,
  MATERIALS_ACCUMULATOR_DOMAINS,
} from '../../../../config/globalSearchConsts'

type searchResult = {
  id: string
  domain: string
  description: string
  entityType: string
  score: number
}

export const GlobalSearchSlot = () => {
  const navigate = useNavigate()
  const [index, setIndex] = useState<number>(-1)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [showQuickSearchDropdown, setShowQuickSearchDropdown] = useState(false)
  const getBoldTextForSearch = (text: string) => getBoldText(text, searchTerm)

  const preventQuickSearchToggle = preventToggleWhenOpen(
    showQuickSearchDropdown,
  )
  const {
    data: recentSearchesResponse,
    loading: recentSearchesLoading,
    error: recentSearchesError,
  } = useQuery(FEDERATED_GET_RECENT_SEARCHES, {
    variables: { page: 0, size: 3 },
  })

  const [
    getGlobalSearches,
    {
      data: globalTopLevelSearchResponse,
      loading: globalSearchLoading,
      error: globalSearchError,
    },
  ] = useLazyQuery(FEDERATED_GET_GLOBAL_TOP_LEVEL_SEARCHES)

  const [deleteRecentSearch] = useMutation(FEDERATED_DELETE_RECENTLY_SEARCHED, {
    refetchQueries: RECENT_SEARCH_REFETCH_QUERIES,
    awaitRefetchQueries: true,
  })

  const [addRecentlySearched] = useMutation(FEDERATED_ADD_RECENTLY_SEARCHED, {
    refetchQueries: RECENT_SEARCH_REFETCH_QUERIES,
    awaitRefetchQueries: true,
  })

  const recentSearchesData =
    recentSearchesResponse?.recentSearches?.content ?? []

  const totalSearchResults = (
    globalTopLevelSearchResponse?.globalTopLevelSearch?.results ?? []
  ).reduce((total, result) => total + (result.pageInfo?.totalElements ?? 0), 0)

  const formattedTotalSearchResults = totalSearchResults.toLocaleString('en-US')

  const groupedSearchDomains = (
    globalTopLevelSearchResponse?.globalTopLevelSearch?.results ?? []
  )
    .flatMap((result) => result.content ?? [])
    .reduce<Record<string, searchResult[]>>((accumulator, item) => {
      if (!item?.domain) {
        return accumulator
      }

      const itemDomainUpper = item.domain.toUpperCase()

      const isMaterialsAccumulatorDomain = MATERIALS_ACCUMULATOR_DOMAINS.some(
        (domain) => domain.toUpperCase() === itemDomainUpper,
      )
      const isDevelopmentAccumulatorDomain =
        DEVELOPMENT_ACCUMULATOR_DOMAINS.some(
          (domain) => domain.toUpperCase() === itemDomainUpper,
        )

      const accumulatorDomain = isMaterialsAccumulatorDomain
        ? 'MATERIALS'
        : isDevelopmentAccumulatorDomain
          ? 'Product Development'
          : item.domain

      if (!accumulator[accumulatorDomain]) {
        accumulator[accumulatorDomain] = []
      }

      accumulator[accumulatorDomain].push(item)
      return accumulator
    }, {})

  const firstTwoRelevantSearchDomains = Object.entries(groupedSearchDomains)
    .map(([domain, content]) => ({
      domain,
      content: [...content]
        .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
        .slice(0, 2),
    }))
    .sort((a, b) => (b.content[0]?.score ?? 0) - (a.content[0]?.score ?? 0))
    .slice(0, 2)

  const flattenedDomainItems = firstTwoRelevantSearchDomains.flatMap(
    (domain) => domain.content,
  )

  const totalSelectableItems =
    recentSearchesData.length + flattenedDomainItems.length

  const handleSelectRecentSearch = (selectedSearchText: string) => {
    addRecentlySearched({ variables: { searchTerm: selectedSearchText } })
    navigate('/searchResults/' + selectedSearchText)
    setShowQuickSearchDropdown(false)
    setSearchTerm('')
    setIndex(-1)
  }

  const handleDeleteRecentSearch = async (id: string) => {
    await deleteRecentSearch({ variables: { id } })
  }

  const selectDomainSearchResult = (item: searchResult) => {
    setShowQuickSearchDropdown(false)
    const { url } = getResourceLink(item.id, item.domain)
    navigate(url)
  }

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setIndex(-1)
    if (!value.trim()) {
      return
    }
    getGlobalSearches({
      variables: {
        page: 0,
        searchText: value,
        size: 2,
      },
    })
  }

  return (
    <>
      <DropdownShell
        isOpen={showQuickSearchDropdown}
        onClose={() => setShowQuickSearchDropdown(false)}
        className={`${styles.dropdownShellContent}`}
      >
        <DropdownShell.Toggle>
          <Input.Text
            value={searchTerm}
            placeholder="Search"
            id="quick-search-input"
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => setShowQuickSearchDropdown(true)}
            onPointerDownCapture={preventQuickSearchToggle}
            onClickCapture={preventQuickSearchToggle}
            onClick={() => setShowQuickSearchDropdown(true)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              handleKeyDown(e, {
                totalSelectableItems,
                setShowQuickSearchDropdown,
                setIndex,
                recentSearchesData,
                setSearchTerm,
                index,
                searchTerm,
                flattenedDomainItems,
                handleSelectRecentSearch,
                selectDomainSearchResult,
              })
            }}
          />
        </DropdownShell.Toggle>
        <DropdownShell.Content
          size={{ xs: true }}
          className={`hc8-pa-3x hc8-mt-none hc8-pb-none ${styles.contentHeight}`}
        >
          {firstTwoRelevantSearchDomains.length === 0 && !searchTerm && null}
          {searchTerm && !globalSearchLoading && !globalSearchError && (
            <Grid.Item className="hc8-pb-2x">
              <ViewAllResults
                totalResults={formattedTotalSearchResults}
                searchTerm={searchTerm}
                setShowQuickSearchDropdown={setShowQuickSearchDropdown}
              />
              <Separator />
            </Grid.Item>
          )}
          {!globalSearchLoading &&
            !recentSearchesLoading &&
            !recentSearchesError && (
              <RecentSearches
                setIndex={setIndex}
                searchIndex={index}
                getBoldText={getBoldTextForSearch}
                setSearchTerm={setSearchTerm}
                setInputFocus={setInputFocus}
                recentSearches={recentSearchesData}
                handleSelectRecentSearch={handleSelectRecentSearch}
                handleDeleteRecentSearch={handleDeleteRecentSearch}
              />
            )}
          {firstTwoRelevantSearchDomains.length === 0 &&
            globalSearchLoading && (
              <Grid.Container
                justify="center"
                className={styles.loadingContainer}
              >
                <Spinner className="hc8-ma-2x" />
              </Grid.Container>
            )}
          {(globalSearchError || recentSearchesError) && (
            <Grid.Item
              align="center"
              justify="center"
              className="hc8-pa-2x"
              size={{ xs: true }}
            >
              <strong>Something went wrong.</strong>
            </Grid.Item>
          )}
          {searchTerm && !globalSearchLoading && !globalSearchError && (
            <>
              {firstTwoRelevantSearchDomains.map((searchGroup, groupIndex) => {
                const startIndex =
                  recentSearchesData.length +
                  firstTwoRelevantSearchDomains
                    .slice(0, groupIndex)
                    .reduce(
                      (count, searchGroup) =>
                        count + searchGroup.content.length,
                      0,
                    )

                return (
                  <DomainGroup
                    key={searchGroup.domain}
                    domainName={searchGroup.domain}
                    content={searchGroup.content}
                    getBoldText={getBoldTextForSearch}
                    searchIndex={index}
                    startIndex={startIndex}
                    setIndex={setIndex}
                    onSelectItem={selectDomainSearchResult}
                    setShowQuickSearchDropdown={setShowQuickSearchDropdown}
                    searchTerm={searchTerm}
                    icon={getDomainIcon(searchGroup.domain)}
                    iconColor={getDomainIconColor(searchGroup.domain)}
                  />
                )
              })}
            </>
          )}
        </DropdownShell.Content>
      </DropdownShell>
    </>
  )
}
